/**
 * ReferralShareCard — V1.5.8 Resume Studio referral loop.
 *
 * Renders on the paid confirmation screen:
 *   "Refer 5 friends → earn ₹49 back"
 * with the caller's share link + Copy / WhatsApp / Email buttons and a
 * live progress bar (N of 5 friends paid → ₹Y credited).
 *
 * Backend contract:
 *   GET /api/resume-studio/referrals/mine?email=X
 *     → { code, stats: { paid_conversion_count, credit_balance_paise,
 *                        credit_earned_paise, goal_conversions,
 *                        lifetime_max_credit_paise } }
 */
import React, { useEffect, useState } from "react";
import { API } from "@/lib/api";
import { toast } from "sonner";
import { Copy, WhatsappLogo, EnvelopeSimple, Gift, CheckCircle } from "@phosphor-icons/react";

const rupees = (paise) => `₹${Math.floor((paise || 0) / 100)}`;

export default function ReferralShareCard({ email }) {
    const [state, setState] = useState({ loading: true, code: null, stats: null, error: null });
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        if (!email) return;
        const url = `${API}/resume-studio/referrals/mine?email=${encodeURIComponent(email)}`;
        fetch(url)
            .then((r) => r.json().then((d) => ({ ok: r.ok, d })))
            .then(({ ok, d }) => {
                if (!ok) throw new Error(d.detail || "Couldn't load referral code");
                setState({ loading: false, code: d.code, stats: d.stats, error: null });
            })
            .catch((e) => setState({ loading: false, code: null, stats: null, error: e.message }));
    }, [email]);

    if (state.loading) {
        return (
            <div data-testid="rs-referral-card-loading"
                 className="rounded-lg border border-cobalt/20 bg-cobalt/5 p-5 animate-pulse">
                <div className="h-4 w-40 bg-cobalt/20 rounded mb-3" />
                <div className="h-3 w-full bg-cobalt/10 rounded" />
            </div>
        );
    }
    if (state.error || !state.code) {
        return null; // Fail-soft: the referral card is a bonus, never a blocker.
    }

    const { code, stats } = state;
    const shareUrl = `${window.location.origin}/resume?ref=${code}`;
    const paid = stats.paid_conversion_count || 0;
    const goal = stats.goal_conversions || 5;
    const credited = stats.credit_earned_paise || 0;
    const balance = stats.credit_balance_paise || 0;
    const cap = stats.lifetime_max_credit_paise || 4900;
    const capHit = credited >= cap;
    const pct = Math.min(100, Math.round((paid / goal) * 100));

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(shareUrl);
            setCopied(true);
            toast.success("Link copied — share it with a friend!");
            setTimeout(() => setCopied(false), 2000);
        } catch {
            toast.error("Couldn't copy — long-press to select the link");
        }
    };
    const shareVia = (channel) => {
        const msg = `I got my CV rewritten by AI on NaukriDaddy — genuinely worth it. Here's a free CV check on me: ${shareUrl}`;
        if (channel === "whatsapp") {
            window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank", "noopener");
        } else if (channel === "email") {
            const subj = "Free AI CV check on NaukriDaddy 🎁";
            window.location.href = `mailto:?subject=${encodeURIComponent(subj)}&body=${encodeURIComponent(msg)}`;
        }
    };

    return (
        <div data-testid="rs-referral-card"
             className="rounded-lg border-2 border-cobalt/30 bg-gradient-to-br from-cobalt/5 to-teal-50 p-5 md:p-6">
            <div className="flex items-start gap-3 mb-4">
                <div className="shrink-0 rounded-full bg-cobalt/10 p-2">
                    <Gift size={22} weight="duotone" className="text-cobalt" />
                </div>
                <div>
                    <h3 className="text-base md:text-lg font-black text-slate-900 tracking-tight leading-tight">
                        Refer 5 friends → earn <span className="text-cobalt">₹49 back</span>
                    </h3>
                    <p className="text-xs md:text-sm text-slate-600 mt-0.5">
                        ₹10 credit per paying friend, capped at ₹49. Redeemable on your next Resume Studio upgrade.
                    </p>
                </div>
            </div>

            {/* Progress bar */}
            <div className="mb-4">
                <div className="flex items-baseline justify-between text-xs mb-1.5">
                    <span className="font-bold text-slate-800" data-testid="rs-referral-progress-label">
                        {paid} of {goal} friends paid
                    </span>
                    <span className="text-slate-500">
                        You've earned {rupees(credited)} · Balance {rupees(balance)}
                    </span>
                </div>
                <div className="h-2 w-full bg-slate-200 rounded-full overflow-hidden">
                    <div
                        data-testid="rs-referral-progress-bar"
                        className="h-full bg-gradient-to-r from-cobalt to-teal-500 transition-all"
                        style={{ width: `${pct}%` }}
                    />
                </div>
                {capHit && (
                    <div className="mt-2 inline-flex items-center gap-1 text-[11px] font-bold text-emerald-700">
                        <CheckCircle size={12} weight="fill" /> Max ₹49 lifetime credit earned
                    </div>
                )}
            </div>

            {/* Share link + actions */}
            <div className="rounded-md bg-white border border-slate-200 p-3">
                <p className="text-[10px] font-black uppercase tracking-wider text-slate-500 mb-2">
                    Your link
                </p>
                <div className="flex items-center gap-2">
                    <code data-testid="rs-referral-share-url"
                          className="flex-1 text-xs md:text-sm text-slate-800 font-mono truncate">
                        {shareUrl}
                    </code>
                </div>
            </div>

            <div className="grid grid-cols-3 gap-2 mt-3">
                <button
                    type="button"
                    onClick={copy}
                    data-testid="rs-referral-copy-btn"
                    className="border border-slate-300 hover:border-cobalt hover:bg-cobalt/5 text-slate-800 px-3 py-2 text-xs font-black uppercase tracking-wider inline-flex items-center justify-center gap-1.5 transition-colors rounded-md"
                >
                    <Copy size={14} weight="bold" />
                    {copied ? "Copied" : "Copy"}
                </button>
                <button
                    type="button"
                    onClick={() => shareVia("whatsapp")}
                    data-testid="rs-referral-whatsapp-btn"
                    className="border border-emerald-300 hover:border-emerald-500 hover:bg-emerald-50 text-emerald-700 px-3 py-2 text-xs font-black uppercase tracking-wider inline-flex items-center justify-center gap-1.5 transition-colors rounded-md"
                >
                    <WhatsappLogo size={14} weight="bold" />
                    WhatsApp
                </button>
                <button
                    type="button"
                    onClick={() => shareVia("email")}
                    data-testid="rs-referral-email-btn"
                    className="border border-slate-300 hover:border-cobalt hover:bg-cobalt/5 text-slate-800 px-3 py-2 text-xs font-black uppercase tracking-wider inline-flex items-center justify-center gap-1.5 transition-colors rounded-md"
                >
                    <EnvelopeSimple size={14} weight="bold" />
                    Email
                </button>
            </div>

            <p className="text-[10px] text-slate-500 mt-3 leading-relaxed">
                Your friend gets a <b>free AI ATS check</b> when they land via your link.
                You earn <b>₹10 credit</b> the moment they pay for any tier. Credit auto-applies at your next checkout.
            </p>
            <a
                href={`/resume/referrals?email=${encodeURIComponent(email)}`}
                data-testid="rs-referral-view-history-link"
                className="mt-3 inline-block text-xs font-black uppercase tracking-wider text-cobalt hover:text-cobalt-700 underline underline-offset-2"
            >
                View full referral history →
            </a>
        </div>
    );
}
